/**
 * Ant Design theme for LexSide — derived from the shared, framework-neutral
 * tokens at design/tokens.json (imported via the @design alias). This is the
 * Ant-specific glue; it lives in the frontend, not in the shared design folder.
 *
 *   import { ConfigProvider } from 'antd';
 *   import { lexTheme } from './theme';
 *   <ConfigProvider theme={lexTheme('light')}> … </ConfigProvider>
 *
 * The three trust states (sealed / review / egress) map onto Ant's
 * success / warning / error, so the whole component library speaks the
 * product's confidence language for free.
 */
import type { ThemeConfig } from 'antd';
import { theme as antTheme } from 'antd';
import tokens from '@design/tokens.json';

type Mode = 'light' | 'dark';

const c = (mode: Mode) => tokens.color[mode] as Record<string, string>;
const FONT_SANS = tokens.font.sans;

export function lexTheme(mode: Mode): ThemeConfig {
  const t = c(mode);
  return {
    algorithm: mode === 'dark' ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    token: {
      colorPrimary: t.primary,
      colorInfo: t.primary,
      // Set the semantic *-Bg/-Border tokens explicitly from the design
      // tokens: antd's palette generator derives them otherwise, and it turns
      // the low-saturation sealed teal into a muddy gray-green in light mode
      // (visible on the zone status Tag).
      colorSuccess: t.sealed,
      colorSuccessBg: t['sealed-bg'],
      colorSuccessBorder: t.sealed,
      colorWarning: t.review,
      colorWarningBg: t['review-bg'],
      colorWarningBorder: t.review,
      colorError: t.egress,
      colorErrorBg: t['egress-bg'],
      colorErrorBorder: t.egress,
      colorLink: t.primary,
      colorText: t.text,
      colorTextSecondary: t['text-muted'],
      colorBgLayout: t['bg-app'],
      colorBgContainer: t.surface,
      colorBorder: t.border,
      borderRadius: 6,
      borderRadiusSM: 4,
      borderRadiusLG: 10,
      // The CSS variable, so the :lang(zh-Hans) stack in tokens.css reaches antd's components too (i18n spec §3.9).
      // eslint-disable-next-line i18next/no-literal-string -- a CSS value
      fontFamily: 'var(--sans)',
      fontSize: 14,
      controlHeight: 36,
      wireframe: false,
    },
    components: {
      Layout: {
        headerBg: t['header-bg'],
        headerColor: t['header-text'],
        siderBg: t.surface,
      },
      // eslint-disable-next-line i18next/no-literal-string -- a CSS value
      Button: { primaryShadow: 'none', fontWeight: 600 },
      Menu: { itemSelectedColor: t.primary, itemSelectedBg: mode === 'dark' ? '#182338' : '#EDF1F7' },
      Table: { headerBg: mode === 'dark' ? '#182338' : '#F4F6F9', borderColor: t.border },
      Tag: { defaultBg: mode === 'dark' ? '#182338' : '#F1F4F8' },
      Card: { headerFontSize: 16 },
    },
  };
}

/** Brass "seal" accent — apply to the single highest-trust affordance on a screen. */
export const seal = { color: tokens.color.brand['brass-500'], colorHover: tokens.color.brand['brass-400'] };
export const fonts = { sans: FONT_SANS };
